import throttle from 'lodash.throttle';

import { SetLowPowerMode } from '@/v2/components/utils/DeviceUtils';

import { actionStrings } from '@/v2/state/momentState';

export default class VideoController {
    static FRAMES = 30;

    static MINIMUM_BUFFER = 0.25;

    // Default scene to play the complete video from beginning to end.
    // Used in case no scenes have been defined in the config.
    static DEFAULT_SCENE = {
        id: '',
        time: { start: 0, end: 0 },
        loop: false,
    };

    // Default optional options for our video controller
    static defaultOptions = {
        bufferBeforePlay: false,
        onFinished: () => {},
        onPlay: () => {},
        onReady: () => {},
        manualPlayback: false,
        autoPlay: true,
        loop: false,
        minimumBuffer: VideoController.MINIMUM_BUFFER,
        playbackRate: 1,
    };

    AddOnLoadedMetaData = (f) => {
        this.video.addEventListener('loadedmetadata', f);
    };

    RemoveOnLoadedMetaData = (f) => {
        this.video.removeEventListener('loadedmetadata', f);
    };

    AddOnCanPlay = (f) => {
        if (this.checkBuffer(f)) return;

        this.video.addEventListener('progress', this.onVideoProgress.bind(this, f));

        this.video.addEventListener('canplaythrough', this.onVideoCanPlayThrough.bind(this, f));

        /*
        if (this.shakaPlayer) {
            f();
        } else {
            this.video.addEventListener('canplay', f);
        }
         */
    };

    onBodyClick = () => {
        SetLowPowerMode(false);

        this.SeekFrame(0);
        this.Play();

        document.removeEventListener('click', this.onBodyClick);
        document.removeEventListener('touchend', this.onBodyClick);
    };

    onVideoProgress = (f) => {
        if (!this.isReady) {
            this.checkBuffer(f);
        }
    };

    onVideoCanPlayThrough = (f) => {
        if (!this.isReady) {
            this.video.removeEventListener('progress', this.onVideoProgress.bind(this, f));
            f();
        }
    };

    /**
     * Video play promise is not resolved in iOS
     * when low power mode is on
     */
    onVideoError = () => {
        SetLowPowerMode(true);

        document.addEventListener('click', this.onBodyClick);
        document.addEventListener('touchend', this.onBodyClick);
    };

    RemoveOnCanPlay = (f) => {
        // if (this.shakaPlayer) {
        // } else {
        // this.video.removeEventListener('canplay', f);
        // }
        this.video.removeEventListener('progress', () => {
            this.checkBuffer(f);
        });
    };

    AddOnTimeUpdated = (f) => {
        this.video.addEventListener('timeupdate', f);
    };

    ProgressDispatch = (dispatch, throttleTimeOut = 800) => {
        this.videoProgressIndicator = () => {
            dispatch({
                type: actionStrings.activeMomentProgress,
                payload: (this.video.currentTime / this.video.duration || 0) * 100,
            });
        };

        this.video.addEventListener('timeupdate', throttle(this.videoProgressIndicator, throttleTimeOut));
    };

    RemoveOnTimeUpdated = (f) => {
        this.video.removeEventListener('timeupdate', f);
    };

    AddOnEnded = (f) => {
        this.video.addEventListener('ended', f, false);
    };

    RemoveOnEnded = (f) => {
        this.video.removeEventListener('ended', f);
    };

    checkBuffer = (f) => {
        if (!this.options) return false;

        let range = 0;
        const bf = this.video.buffered;
        const time = this.video.currentTime;

        if (bf.length) {
            while (range < bf.length && !(bf.start(range) <= time && time <= bf.end(range))) {
                range += 1;
            }

            range = bf.length - 1;

            // const loadStartPercentage = bf.start(range) / this.video.duration;
            const loadEndPercentage = bf.end(range) / this.video.duration;
            const loadPercentage = loadEndPercentage;
            // const loadPercentage = loadEndPercentage - loadStartPercentage;
            if (loadPercentage >= this.options.minimumBuffer) {
                this.video.removeEventListener('canplaythrough', this.onVideoCanPlayThrough.bind(this, f));
                f();
                return true;
            }
        }

        return false;
    };

    constructor(config, videoElement, shakaPlayer, options = VideoController.defaultOptions) {
        this.config = config;
        this.video = videoElement;
        this.options = { ...VideoController.defaultOptions, ...options };
        this.currentScene = null;
        this.currentHotspot = null;
        this.shakaPlayer = shakaPlayer;

        this.metaDataLoaded = false;
        this.canPlayThrough = false;

        this.init();
        this.playPromise = undefined;
        this.isPlaying = false;

        this.video.loop = this.options.loop;
        this.video.playbackRate = this.options.playbackRate;
        this.onFrame = [];
        this.isReady = false;

        this.videoProgressIndicator = null;
    }

    AddOnFrame(frame, cb) {
        this.onFrame.push({ frame, cb });
    }

    Kill() {
        this.options = null;
        // this.RemoveOnEnded(this.onEnd);
        this.RemoveOnTimeUpdated(this.onTime);
        this.RemoveOnTimeUpdated(this.videoProgressIndicator);
        this.RemoveOnCanPlay();
    }

    // Load a specific scene from the video file. Scenes are defined in the videos config.json
    LoadScene(id, play = false) {
        this.LoadSceneByObject(this.getById(this.config.scenes, id), play);
    }

    LoadSceneByObject(scene, play = false) {
        this.currentScene = scene;

        if (scene != null) {
            // In case no scene hs been found. Do nothing.
            this.fixEndTime(); // In case the video has 0 as end time.
            // Make sure to play video until end of actual video file.

            this.SeekFrame(this.currentScene.time.start);
            if (play) this.Play();
        }
    }

    LoadHotspot(id) {
        this.currentHotspot = this.getById(this.currentScene.hotspots, id);

        // Start playing hotspot from specific position in our video.
        this.SeekFrame(this.currentHotspot.time.start);
        this.Play();
    }

    unloadHotspot() {
        if (this.currentHotspot == null) return;
        this.currentHotspot = null;
        this.LoadScene(this.currentScene.id, true);
    }

    Play(time) {
        if (time != null) this.SeekTime(time); // Start playing video from specific time.

        if (!this.options?.manualPlayback) {
            // If we want to control the moving of video frames using code.

            this.playPromise = this.video.play(); // For exmple when scrolling or a slider.
            this.isPlaying = true;

            if (this.playPromise !== undefined) {
                this.playPromiseReturned = false;
                this.playPromise
                    .then(() => {
                        this.playPromiseReturned = true;
                    })
                    .catch(() => {
                        this.onVideoError();
                    });
            }
        }
    }

    Pause() {
        if (this.playPromise !== undefined) {
            if (this.playPromiseReturned) {
                this.video.pause();
                this.isPlaying = false;
            }
        } else {
            this.video.pause();
            this.isPlaying = false;
        }
    }

    // Normalized scrubbing between 0 - 1.
    SeekPercentage(percentage) {
        const endTimeNormalized = (this.currentScene.time.end - this.currentScene.time.start) * parseFloat(percentage);

        this.SeekFrame(this.currentScene.time.start + endTimeNormalized);
    }

    InitPoster() {
        if (!this.options.autoPlay) {
            this.AddOnFrame(1, () => {
                if (!this.isPlaying) {
                    this.video.pause();
                    this.SeekFrame(1);
                }
            });
            this.video.play();
        }
    }

    // Scrub by time.
    SeekTime(time) {
        if (time !== this.video.currentTime) {
            this.video.currentTime = time;
        }
    }

    // Scrub by frame.
    SeekFrame(frame) {
        this.SeekTime(this.framesToTime(frame));
    }

    GetFrame() {
        return this.timeToFrames(this.GetTime());
    }

    GetTime() {
        return this.video.currentTime;
    }

    GetEndTime() {
        return this.framesToTime(this.currentScene.time.end);
    }

    init = () => {
        // needed to get the length and other data of the video.
        this.AddOnLoadedMetaData(this.onMetaDataLoaded);
    };

    onCanPlayThrough = () => {
        if (this.canPlayThrough) return;

        this.RemoveOnCanPlay(this.onCanPlayThrough);
        this.canPlayThrough = true;

        this.onReady();
    };

    onMetaDataLoaded = () => {
        this.metaDataLoaded = true;
        this.onReady();

        if (this.options?.bufferBeforePlay) {
            this.AddOnCanPlay(this.onCanPlayThrough);
        }

        this.RemoveOnLoadedMetaData(this.onMetaDataLoaded);
    };

    onReady = () => {
        if (this.metaDataLoaded && (this.canPlayThrough || this.options?.bufferBeforePlay === false)) {
            // check if we only have one scene defined. If so, load it by default.
            this.loadDefault();

            if (this.options?.onReady) this.options.onReady(this);

            // this.AddOnEnded(this.onEnd.bind(this));
            this.AddOnTimeUpdated(this.onTime.bind(this));

            this.isReady = true;
        }
    };

    loadDefault = () => {
        // check if we only have one scene defined. If so, load it by default.
        // Otherwise do nothing and wait for manual loading of scene.

        let scene = JSON.parse(JSON.stringify(VideoController.DEFAULT_SCENE));
        scene.loop = this.options?.loop;

        if (this.config.scenes != null) {
            // if config has at least one scene defined.
            scene =
                this.config.scenes.length === 1
                    ? this.config.scenes[0] // only one scene?
                    : this.getById(this.config.scenes, 'default'); // one scene which is default?
        }

        if (scene != null) {
            this.LoadSceneByObject(scene, this.options?.autoPlay);
        }
    };

    fixEndTime = () => {
        if (this.currentScene != null && this.currentScene.time.end === 0) {
            // console.log(`fix end ${this.video.src}`, this.video.duration);
            this.currentScene.time.end = this.timeToFrames(this.video.duration);
        }
    };

    getById = (data, id) => {
        // Get a scene or hotspot by id from config.
        const scenes = data != null ? data.filter((item) => item.id === id) : [];
        return scenes.length ? scenes[0] : null;
    };

    onTime = () => {
        // if we are currently playing  scene or a hotspot.
        const loopPoint = this.currentHotspot != null ? this.currentHotspot : this.currentScene;

        if (this.video.currentTime === 0) return;

        const currentFrame = Math.ceil(this.timeToFrames(this.video.currentTime));

        if (this.onFrame.length > 0 && this.onFrame[0].frame <= currentFrame) {
            this.onFrame[0].cb();
            this.onFrame.shift();
        }

        // If we've reached the end time.
        if (currentFrame >= Math.floor(loopPoint.time.end)) {
            this.onEnd();
        }
    };

    onEnd = () => {
        if (this.options?.onFinished != null) this.options.onFinished(); // notify any callback.

        if (this.currentHotspot == null && this.currentScene.loop) {
            // if we are looping a part of the video.
            this.Play(this.currentScene.time.start);
            return; // looping, break function.
        }

        this.Pause(); // finished playing.
        this.unloadHotspot(); // unload hotspot if we have one loaded.
    };

    framesToTime = (frames) => frames / VideoController.FRAMES;

    timeToFrames = (time) => time * VideoController.FRAMES;
}
